import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CallOutcome } from '@acs/shared';
import {
  Avatar,
  Badge,
  Button,
  ChatBubble,
  EmptyState,
  Icon,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  Skeleton,
  Transcript,
} from '@acs/ui';
import type { BadgeTone } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import { subscribeCalls, type CallDateRange } from '../../shared/api/calls.js';
import type { CallDoc } from '../../shared/api/converters.js';
import { keys } from '../../shared/api/keys.js';
import { useLiveQuery } from '../../shared/api/useLiveQuery.js';
import { formatDateTime, formatDurationSec, formatTime } from '../../shared/lib/format.js';

const OUTCOME_TONE: Record<CallOutcome, BadgeTone> = {
  booked: 'success',
  // Changing an existing appointment is a successful call, not a lost one
  // (doc 14 D5) — a cancellation is warned rather than celebrated.
  rescheduled: 'success',
  cancelled: 'warning',
  no_booking: 'warning',
  abandoned: 'neutral',
  error: 'danger',
};

/** Outcomes read better with a space than with the raw enum value. Ordered —
 *  this is what fills the filter dropdown. */
const OUTCOMES: CallOutcome[] = ['booked', 'rescheduled', 'cancelled', 'no_booking', 'abandoned', 'error'];

const outcomeKey = (o: CallOutcome) => `calls.outcome.${o}` as TranslationKey;

const DAY_MS = 86_400_000;

type PresetId = 'all' | 'today' | '7d' | '30d';

const PRESETS: PresetId[] = ['today', '7d', '30d', 'all'];

function rangeFor(preset: PresetId): CallDateRange {
  if (preset === 'all') return {};
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (preset === '7d') return { from: new Date(start.getTime() - 6 * DAY_MS) };
  if (preset === '30d') return { from: new Date(start.getTime() - 29 * DAY_MS) };
  return { from: start };
}

/**
 * Call history (design template — Portal · Call History): a preset-driven date
 * filter over a scannable row list, with the full transcript one click away.
 */
export function CallsPage() {
  const { t, tag } = useI18n();
  const business = useActiveBusiness();
  const navigate = useNavigate();

  const [preset, setPreset] = useState<PresetId>('7d');
  const [outcome, setOutcome] = useState<CallOutcome | 'all'>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<CallDoc | null>(null);

  const range = useMemo(() => rangeFor(preset), [preset]);
  const { data, isLoading, isError } = useLiveQuery<CallDoc[]>(keys.calls(business.id, range), (cb) =>
    subscribeCalls(business.id, range, cb),
  );

  const all = data ?? [];
  // Outcome and free text filter locally: the row cap is small and it keeps the
  // Firestore query index-free (range predicate on the sort field only).
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((c) => {
      if (outcome !== 'all' && c.outcome !== outcome) return false;
      if (!q) return true;
      return (c.from ?? '').toLowerCase().includes(q) || c.transcript.some((t) => t.text.toLowerCase().includes(q));
    });
  }, [all, outcome, query]);

  const filtered = outcome !== 'all' || query.trim().length > 0;

  return (
    <>
      <PageHeader
        title={t('calls.title')}
        subtitle={t('calls.subtitle')}
        actions={
          <Button leadingIcon={<Icon name="phone" size={16} />} onClick={() => navigate(`/b/${business.id}/test`)}>
            {t('calls.testCall')}
          </Button>
        }
      />

      <div className="tc-card" style={{ marginBottom: 16, padding: 14 }}>
        <div className="calls-filter" style={{ marginBottom: 12 }}>
          <div style={{ flex: '1 1 240px', minWidth: 200 }}>
            <SearchInput
            clearLabel={t('ui.clearSearch')}
              value={query}
              onValueChange={setQuery}
              placeholder={t('calls.searchPlaceholder')}
              aria-label={t('calls.searchLabel')}
            />
          </div>
          <Select
            aria-label={t('calls.filterByOutcome')}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as CallOutcome | 'all')}
          >
            <option value="all">{t('calls.allOutcomes')}</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {t(outcomeKey(o))}
              </option>
            ))}
          </Select>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t('calls.period')}</span>
          {PRESETS.map((id) => (
            <Button
              key={id}
              size="sm"
              variant={preset === id ? 'primary' : 'secondary'}
              onClick={() => setPreset(id)}
            >
              {t(`calls.preset.${id}` as TranslationKey)}
            </Button>
          ))}
        </div>
      </div>

      {isError ? (
        <EmptyState
          icon={<Icon name="alert-circle" size={24} />}
          iconTone="danger"
          title={t('calls.error.title')}
          description={t('calls.error.description')}
        />
      ) : isLoading ? (
        <div className="tc-cardlist">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={68} radius="var(--tc-radius-md)" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="phone" size={24} />}
          title={filtered ? t('calls.empty.filtered') : t('calls.empty.none', { business: business.name })}
          description={t(filtered ? 'calls.empty.filteredDescription' : 'calls.empty.noneDescription')}
          actions={
            filtered ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setQuery('');
                  setOutcome('all');
                  setPreset('all');
                }}
              >
                {t('calls.clearFilters')}
              </Button>
            ) : (
              <Button onClick={() => navigate(`/b/${business.id}/test`)}>{t('calls.makeTestCall')}</Button>
            )
          }
        />
      ) : (
        <>
          <div className="tc-cardlist">
            {rows.map((call) => (
              <CallRow
                key={call.id}
                call={call}
                timezone={business.timezone}
                locale={tag}
                onOpen={() => setSelected(call)}
              />
            ))}
          </div>
          <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
            {rows.length === all.length
              ? t('calls.showing', { count: rows.length, shown: rows.length })
              : t('calls.showingOf', { count: rows.length, shown: rows.length, total: all.length })}
            {all.length >= 50 ? t('calls.narrowPeriod') : '.'}
          </p>
        </>
      )}

      {selected && (
        <CallDetail call={selected} timezone={business.timezone} locale={tag} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function CallRow({
  call,
  timezone,
  locale,
  onOpen,
}: {
  call: CallDoc;
  timezone: string;
  locale: string;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      className="tc-cardlist__item call-row"
      onClick={onOpen}
      style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', font: 'inherit' }}
    >
      <CallerAvatar call={call} />
      <div style={{ flex: '1 1 140px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--tc-text)' }}>{callerName(call, t)}</div>
        <div className="mono muted" style={{ fontSize: 12 }}>
          {formatDateTime(call.startedAt, timezone, locale)} · {duration(call.durationSec, t)}
        </div>
      </div>
      {call.test && <Badge tone="info" size="sm">{t('calls.test')}</Badge>}
      {call.error && call.outcome !== 'error' && (
        // A technical error is a separate signal from the booking outcome: a
        // call can book AND hit a transient error.
        <Badge tone="warning" size="sm" title={call.error}>
          <Icon name="alert-triangle" size={12} /> {t('calls.issue')}
        </Badge>
      )}
      {/* The call ran into something we could not do (doc 23 P0). Shown next
          to the outcome rather than inside it, because the two are independent:
          a call can book an appointment AND fail to answer a question about
          parking, and collapsing that into one verdict loses the half that
          tells us what to build next. */}
      {/* The message the caller left (doc 28). ABOVE the dead-end list on
          purpose: one is something to act on, the other is a statistic. */}
      {call.message && (
        <div className="call-message">
          <div className="call-message__head">
            <Icon name="mail" size={16} />
            {t('calls.message.title')}
            <Badge tone="neutral" size="sm">
              {t(`message.kind.${call.message.kind}` as TranslationKey)}
            </Badge>
            {call.message.callbackRequested && (
              <Badge tone="warning" size="sm">{t('message.callback')}</Badge>
            )}
          </div>
          <p className="call-message__body">{call.message.summary}</p>
        </div>
      )}

      {call.unmetRequests.length > 0 && (
        <Badge tone="neutral" size="sm" title={call.unmetRequests.map((u) => u.summary).join(' · ')}>
          <Icon name="help-circle" size={12} /> {t('calls.unmet', { count: call.unmetRequests.length })}
        </Badge>
      )}
      {call.outcome ? (
        <Badge tone={OUTCOME_TONE[call.outcome]} dot>
          {t(outcomeKey(call.outcome))}
        </Badge>
      ) : (
        <Badge tone="neutral">{t('calls.inProgress')}</Badge>
      )}
      <Icon name="chevron-right" size={18} className="call-row__chevron" />
    </button>
  );
}

function CallDetail({
  call,
  timezone,
  locale,
  onClose,
}: {
  call: CallDoc;
  timezone: string;
  locale: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <Modal
      closeLabel={t('ui.closeDialog')}
      size="lg"
      onClose={onClose}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <CallerAvatar call={call} />
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span>{callerName(call, t)}</span>
            <span className="mono muted" style={{ fontSize: 12, fontWeight: 500 }}>
              {formatDateTime(call.startedAt, timezone, locale)} · {duration(call.durationSec, t)}
            </span>
          </span>
        </span>
      }
      footer={<Button variant="secondary" onClick={onClose}>{t('calls.close')}</Button>}
    >
      <div className="row" style={{ gap: 8, marginBottom: 16 }}>
        {call.outcome && (
          <Badge tone={OUTCOME_TONE[call.outcome]} dot>
            {t(outcomeKey(call.outcome))}
          </Badge>
        )}
        {call.test && <Badge tone="info">{t('calls.testCallBadge')}</Badge>}
        {call.error && <Badge tone="danger">{call.error}</Badge>}
      </div>

      {/* What the caller wanted and did not get (doc 23 P0).
          Above the transcript on purpose: the transcript is what happened, this
          is the part worth acting on, and an owner scanning a call is looking
          for the second one. Each row is the ASSISTANT's paraphrase, not a
          quote — the label says so, because an owner ringing a customer back
          about "wanted a quote for a respray" needs to know those were not
          their words. */}
      {call.unmetRequests.length > 0 && (
        <div className="unmet">
          <div className="unmet__head">
            <Icon name="help-circle" size={16} />
            {t('calls.unmet.title', { count: call.unmetRequests.length })}
          </div>
          <ul className="unmet__list">
            {call.unmetRequests.map((u, i) => (
              <li key={i}>
                <Badge tone="neutral" size="sm">{t(`calls.unmet.kind.${u.kind}` as TranslationKey)}</Badge>
                <span>{u.summary || t('calls.unmet.noSummary')}</span>
              </li>
            ))}
          </ul>
          <p className="unmet__note">{t('calls.unmet.note')}</p>
        </div>
      )}

      {call.transcript.length === 0 ? (
        <EmptyState
          borderless
          icon={<Icon name="file-text" size={24} />}
          iconTone="neutral"
          title={t('calls.noTranscript')}
          description={t('calls.noTranscriptDescription')}
        />
      ) : (
        <Transcript>
          {call.transcript.map((turn, i) =>
            turn.role === 'tool' ? (
              <details key={i} className="tool-turn">
                <summary>
                  {turn.tool?.name ?? turn.text}
                  {turn.tool?.ok === false ? t('calls.toolFailed') : ''}
                </summary>
                {turn.tool && <pre className="tool-io">{`args: ${turn.tool.args}\n→ ${turn.tool.result}`}</pre>}
              </details>
            ) : (
              <ChatBubble
                key={i}
                speaker={turn.role === 'assistant' ? 'assistant' : 'caller'}
                at={offsetLabel(turn.t)}
              >
                {turn.text}
              </ChatBubble>
            ),
          )}
        </Transcript>
      )}

      <p className="muted" style={{ marginTop: 16, fontSize: 12 }}>
        {t('calls.started', { time: formatTime(call.startedAt, timezone, locale) })}
        {call.endedAt ? t('calls.endedSuffix', { time: formatTime(call.endedAt, timezone, locale) }) : ''}
      </p>
    </Modal>
  );
}

/** A phone number has no initials worth deriving ("+33 6 12…" → "+7"), so an
 *  unnamed caller gets an icon instead. */
function CallerAvatar({ call }: { call: CallDoc }) {
  const { t } = useI18n();
  return (
    <Avatar
      size="sm"
      name={callerName(call, t)}
      icon={<Icon name={call.test ? 'mic' : call.from ? 'phone-incoming' : 'help-circle'} size={16} />}
    />
  );
}

/** A caller we can't name is still a person — label them, don't leave a blank. */
function callerName(call: CallDoc, t: (k: TranslationKey) => string): string {
  if (call.test) return t('calls.testCallBadge');
  return call.from ?? t('calls.withheldNumber');
}

/** `formatDurationSec` with the unit letters the reader's language uses. */
function duration(sec: number | undefined, t: (k: TranslationKey) => string): string {
  return formatDurationSec(sec, { m: t('common.durationMinutes'), s: t('common.durationSeconds') });
}

/** Seconds from call start → `m:ss`. */
function offsetLabel(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
